import React, { useEffect, useState } from "react";
import { AddButton, CancelButton, SubmitButton } from "../../components/mui/Buttons/Buttons";
import { Form, FormikProvider, useFormik } from "formik";
import { Alert, Autocomplete, Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import GlobalDialog from "../../components/mui/Dialogue/GlobalDialog";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  addPermissionRelation,
  addPermissionRelationByDepartment,
  getUsersUnderDepartment,
} from "../../redux/features/permissionSlice";

// Two ways to grant the same permission:
//  - "users": pick specific employees one by one (original flow — grants their usertypes).
//  - "department": grant every user role under the department in one action (added later,
//    kept alongside the original flow rather than replacing it).
const Permissionrelation = ({ department, permission }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("users");

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, usersunderdpt, departments } = useSelector((state) => state.permission);

  const [selectedUsers, setSelectedUsers] = useState([]);

  const departmentName =
    departments.find((d) => d.dept_id === department)?.dept_name || `#${department}`;

  const ValidationSchema = Yup.object().shape({
    userType: Yup.array()
      .of(Yup.number())
      .min(1, "Please select at least one user")
      .required("Please select users"),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      permissionId: permission,
      userType: [],
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      dispatch(addPermissionRelation(values)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, {
            variant: "success",
          });
          formik.resetForm();
          setSelectedUsers([]);
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: "error",
          });
        }
      });
    },
  });

  useEffect(() => {
    if (open && department) dispatch(getUsersUnderDepartment(department));
  }, [open, department, dispatch]);

  const handleusertype = (e, selectedOptions) => {
    const uniqueUsers = Array.from(
      new Map(selectedOptions.map((user) => [user.userID, user])).values()
    );

    setSelectedUsers(uniqueUsers);

    const typeIDs = uniqueUsers.map((user) => user.usertypeID);
    formik.setFieldValue("userType", typeIDs);
  };

  const handleGrantDepartment = () => {
    dispatch(addPermissionRelationByDepartment({ permissionId: permission, departmentId: department })).then(
      (res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || "Could not grant permission", { variant: "error" });
        }
      }
    );
  };

  return (
    <GlobalDialog
      title={"Permission Relation"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Permission Relation
        </AddButton>
      }
      content={
        <Box>
          <Stack spacing={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={mode}
              onChange={(e, value) => value && setMode(value)}
            >
              <ToggleButton value="users">Specific users</ToggleButton>
              <ToggleButton value="department">Entire department</ToggleButton>
            </ToggleButtonGroup>

            {mode === "users" ? (
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Stack direction={"column"} spacing={2}>
                    <Autocomplete
                      fullWidth
                      multiple
                      name="userType"
                      options={usersunderdpt}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedUsers}
                      onChange={handleusertype}
                      renderInput={(params) => (
                        <TextField
                          disabled
                          fullWidth
                          autoComplete="off"
                          {...params}
                          label="User"
                          error={
                            formik.touched.userType &&
                            Boolean(formik.errors.userType)
                          }
                          helperText={
                            formik.touched.userType && formik.errors.userType
                          }
                        />
                      )}
                    />
                  </Stack>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}
                  >
                    <Stack direction={"row"} spacing={1}>
                      <CancelButton size="small" action={() => setOpen(false)}>
                        Cancel
                      </CancelButton>
                      <SubmitButton disabled={loading} size="small" type="submit">
                        {loading ? "Please wait..." : "Save"}
                      </SubmitButton>
                    </Stack>
                  </Box>
                </Form>
              </FormikProvider>
            ) : (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Grant this permission to every user role in <b>{departmentName}</b>. Everyone
                  currently in this department gets access immediately.
                </Typography>

                <Alert severity="info" sx={{ fontSize: "0.75rem" }}>
                  {usersunderdpt?.length
                    ? `${usersunderdpt.length} employee(s) currently in this department will get access.`
                    : "Loading employees in this department…"}{" "}
                  This is a one-time grant — a role added to the department later will need this
                  repeated to pick it up.
                </Alert>

                <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                  <Stack direction={"row"} spacing={1}>
                    <CancelButton size="small" action={() => setOpen(false)}>
                      Cancel
                    </CancelButton>
                    <SubmitButton disabled={loading} size="small" action={handleGrantDepartment}>
                      {loading ? "Please wait..." : "Grant to department"}
                    </SubmitButton>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      }
    />
  );
};

export default Permissionrelation;
